import React, { useEffect, useState } from 'react';
import { doc, onSnapshot, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebaseConfig';
import { rollText, calculateRunningScores, calculateMaxScore, isSplit, isFreshRack10th } from './bowlingScoring';
import './LiveScoreView.css';

const APP_STORE_URL = 'https://apps.apple.com/us/app/advanced-bowling-tracker/id6759532374';

// Android app is live on Google Play. Android viewers are routed here by
// storeUrlForUA; set back to null to send every platform to the App Store.
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.advancedbowlingtracker';

/**
 * Pick the store link for the viewer's platform: Android user agents get the
 * Play Store, everything else — iOS, iPadOS, desktop — gets the App Store.
 * (Falls back to the App Store for every platform if PLAY_STORE_URL is null.)
 */
export function storeUrlForUA(userAgent, playStoreUrl = PLAY_STORE_URL) {
  if (playStoreUrl && /android/i.test(userAgent || '')) {
    return playStoreUrl;
  }
  return APP_STORE_URL;
}

// Heartbeat cadence for watcher presence — mirrors the iOS LiveViewersManager
// (35s heartbeat, 90s staleness on the counting side). Web viewers are
// anonymous, so presence is keyed by a random per-load client id.
const VIEWER_HEARTBEAT_MS = 35000;
const VIEWER_CLIENT_ID = `web_${Math.random().toString(36).slice(2, 12)}`;

export default function LiveScoreView({ liveCode }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | active | ended | notfound

  useEffect(() => {
    const docRef = doc(db, 'liveSessions', liveCode);
    const unsubscribe = onSnapshot(docRef, (snapshot) => {
      if (!snapshot.exists()) {
        setStatus('notfound');
        return;
      }
      const d = snapshot.data();
      setData(d);
      setStatus(d.isActive ? 'active' : 'ended');
    });
    return () => unsubscribe();
  }, [liveCode]);

  // Watcher-presence heartbeat while the session is live. Counts this web
  // viewer toward the bowler's in-app watcher pill. Best-effort delete on
  // leave; otherwise the doc ages out of counts after 90s.
  useEffect(() => {
    if (status !== 'active') return undefined;
    const viewerRef = doc(db, 'liveSessions', liveCode, 'viewers', VIEWER_CLIENT_ID);
    const beat = () => {
      setDoc(viewerRef, { lastSeenAt: serverTimestamp() }).catch(() => {});
    };
    beat();
    const interval = setInterval(beat, VIEWER_HEARTBEAT_MS);
    return () => {
      clearInterval(interval);
      deleteDoc(viewerRef).catch(() => {});
    };
  }, [status, liveCode]);

  if (status === 'loading') {
    return (
      <div className="live-container">
        <div className="live-loading">
          <div className="live-spinner" />
          <p>Loading live scores...</p>
        </div>
      </div>
    );
  }

  if (status === 'notfound') {
    return (
      <div className="live-container">
        <div className="live-not-found">
          <span className="live-not-found-icon">🎳</span>
          <h2>Session not found</h2>
          <p>This live scoring link has expired or doesn't exist.</p>
          <a href="/" className="live-home-link">Go to homepage</a>
        </div>
      </div>
    );
  }

  const { bowlerName, alleyName, laneInfo, isActive, isNoTap,
    games = [], totalPinfall, highGame, averageScore, gameCount,
    leagueName, tournamentName, currentGameNumber, currentFrameNumber } = data;

  // The average counts bowled games only, so it stays blank until the first one
  // lands. Docs from an older app version carry no completedGameCount; counting
  // completed games in the array keeps their average on screen.
  const completedGameCount = typeof data.completedGameCount === 'number'
    ? data.completedGameCount
    : games.filter((g) => g.isComplete).length;

  const storeUrl = storeUrlForUA(typeof navigator !== 'undefined' ? navigator.userAgent : '');

  return (
    <div className="live-container">
      {/* Header */}
      <header className="live-header">
        <a href="https://advancedbowlingtracker.com" className="live-header-left" target="_blank" rel="noopener noreferrer">
          <img src="/app-icon.png" alt="Advanced Bowling Tracker" className="live-app-icon" />
          <span className="live-app-name">Advanced Bowling Tracker</span>
        </a>
        <a href={storeUrl} target="_blank" rel="noopener noreferrer" className="live-download-btn">
          Get the App
        </a>
      </header>

      {/* Session Info */}
      <section className="live-session-info">
        <div className="live-bowler-row">
          <h1 className="live-bowler-name">{bowlerName}</h1>
          <span className={`live-badge ${isActive ? 'live-badge-live' : 'live-badge-final'}`}>
            {isActive && <span className="live-badge-dot" />}
            {isActive ? 'LIVE' : 'FINAL'}
          </span>
        </div>
        <div className="live-meta">
          {alleyName && <span>{alleyName}</span>}
          {laneInfo && <span>{laneInfo}</span>}
          {leagueName && <span>{leagueName}</span>}
          {tournamentName && <span>{tournamentName}</span>}
          {isNoTap && <span className="live-notap-badge">9-Pin No-Tap</span>}
        </div>
      </section>

      {/* Scorecards */}
      {games.length === 0 ? (
        <div className="live-starting-soon">
          <p>Session is starting soon...</p>
        </div>
      ) : (
        <section className="live-games">
          {games.map((game, idx) => (
            <GameScorecard
              key={idx}
              game={game}
              isNoTap={isNoTap}
              currentFrame={
                isActive && !game.isComplete && game.gameNumber === currentGameNumber
                  ? currentFrameNumber
                  : null
              }
            />
          ))}
        </section>
      )}

      {/* Stats */}
      {gameCount > 0 && (
        <section className="live-stats">
          <StatCard label="Total Pinfall" value={totalPinfall} />
          <StatCard label="High Game" value={highGame} />
          <StatCard label="Average" value={completedGameCount > 0 ? Math.round(averageScore) : '—'} />
          <StatCard label="Games" value={gameCount} />
        </section>
      )}

      {/* Footer CTA */}
      <footer className="live-footer">
        <p>Track your own scores with Advanced Bowling Tracker</p>
        <div className="live-footer-btns">
          <a href={storeUrl} target="_blank" rel="noopener noreferrer" className="live-footer-btn">
            Download the App
          </a>
          <a href="https://advancedbowlingtracker.com" target="_blank" rel="noopener noreferrer" className="live-footer-btn live-footer-btn-secondary">
            Learn More
          </a>
        </div>
        <a href="https://advancedbowlingtracker.com" target="_blank" rel="noopener noreferrer" className="live-brandline">
          <img src="/app-icon.png" alt="" />
          advancedbowlingtracker.com
        </a>
      </footer>
    </div>
  );
}

// Firebase Storage only — bagged balls get their catalog image mirrored to
// ballImages/catalog-{id}.jpg by the app (ImageManager), so published
// ballImageUrls are always Firebase-hosted. Third-party CDNs (e.g. the
// bowwwl.com catalog source) are deliberately NOT allowed: no hotlinking.
const ALLOWED_IMAGE_DOMAINS = ['firebasestorage.googleapis.com', 'storage.googleapis.com', 'firebasestorage.app'];

export function isAllowedImageUrl(url) {
  if (!url) return false;
  try {
    const { hostname } = new URL(url);
    // Exact host or subdomain only — substring matching would let
    // e.g. firebasestorage.app.evil.com through.
    return ALLOWED_IMAGE_DOMAINS.some(d => hostname === d || hostname.endsWith('.' + d));
  } catch {
    return false;
  }
}

function BallIcon() {
  return (
    <svg className="live-ball-icon" viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.6" />
      <circle cx="9" cy="8" r="1.5" />
      <circle cx="13" cy="7" r="1.5" />
      <circle cx="13" cy="11" r="1.5" />
    </svg>
  );
}

function GameScorecard({ game, isNoTap, currentFrame = null }) {
  const { gameNumber, frames = [], score, isComplete, ballNames = [], ballImageUrls = {} } = game;
  // Class helper: existing frame-10 marker plus the current-frame highlight
  const frameClass = (i) =>
    [i === 9 ? 'frame-10' : '', currentFrame === i + 1 ? 'frame-current' : '']
      .filter(Boolean).join(' ');
  const runningScores = calculateRunningScores(frames, isNoTap);

  const maxScore = !isComplete && frames.length > 0 ? calculateMaxScore(frames, isNoTap) : null;

  // Find the most recently used ball name
  const activeBallName = (() => {
    if (isComplete) return null;
    for (let i = frames.length - 1; i >= 0; i--) {
      const f = frames[i];
      if (!f.rolls) continue;
      for (let r = f.rolls.length - 1; r >= 0; r--) {
        if (f.rolls[r].ballName) return f.rolls[r].ballName;
      }
    }
    return null;
  })();

  return (
    <div className="live-game-card">
      <div className="live-game-header">
        <span className="live-game-label">Game {gameNumber}</span>
        <span className="live-game-score">
          {(isComplete || score > 0) ? score : ''}
          {maxScore !== null && maxScore !== score && (
            <span className="live-max-score"> (max {maxScore})</span>
          )}
        </span>
      </div>
      {ballNames.length > 0 && (
        <div className="live-ball-info">
          {ballNames.map((name, idx) => {
            const imageUrl = ballImageUrls[name];
            const showImage = isAllowedImageUrl(imageUrl);
            const isActive = name === activeBallName;
            return (
              <span key={idx} className={`live-ball-item ${isActive ? 'live-ball-active' : ''}`}>
                {showImage ? (
                  <img src={imageUrl} alt={name} className="live-ball-img" />
                ) : (
                  <BallIcon />
                )}
                <span className="live-ball-name">{name}</span>
              </span>
            );
          })}
        </div>
      )}
      <div className="live-scorecard">
        <table className="live-scorecard-table">
          <thead>
            <tr>
              {Array.from({ length: 10 }, (_, i) => (
                <th key={i} className={frameClass(i)}>{i + 1}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="pindeck-row">
              {Array.from({ length: 10 }, (_, i) => {
                const frame = frames.find(f => f.number === i + 1);
                const is10th = i === 9;
                const isCurrent = currentFrame === i + 1;
                return (
                  <td key={i} className={frameClass(i)}>
                    <div className={`pindeck-cells ${is10th ? 'pindeck-cells-10th' : ''}`}>
                      {is10th ? (
                        // 10th frame: only show pin decks for rolls that exist
                        <>
                          <FramePinDeck frame={frame} rollIndex={0} isNoTap={isNoTap} isCurrent={isCurrent} />
                          {frame && frame.rolls && frame.rolls.length > 1 && (
                            <FramePinDeck frame={frame} rollIndex={1} isNoTap={isNoTap} isCurrent={isCurrent} />
                          )}
                          {frame && frame.rolls && frame.rolls.length > 2 && (
                            <FramePinDeck frame={frame} rollIndex={2} isNoTap={isNoTap} isCurrent={isCurrent} />
                          )}
                        </>
                      ) : (
                        <FramePinDeck frame={frame} rollIndex={0} isNoTap={isNoTap} isCurrent={isCurrent} />
                      )}
                    </div>
                  </td>
                );
              })}
            </tr>
            <tr className="rolls-row">
              {Array.from({ length: 10 }, (_, i) => {
                const frame = frames.find(f => f.number === i + 1);
                const is10th = i === 9;
                return (
                  <td key={i} className={frameClass(i)}>
                    <div className="roll-boxes">
                      {is10th ? (
                        // 10th frame: 3 boxes
                        <>
                          <RollBox frame={frame} rollIndex={0} isNoTap={isNoTap} />
                          <RollBox frame={frame} rollIndex={1} isNoTap={isNoTap} />
                          <RollBox frame={frame} rollIndex={2} isNoTap={isNoTap} />
                        </>
                      ) : (() => {
                        // Frames 1-9: single centered X for strikes, otherwise 2 boxes
                        const isStrike = frame && (frame.rolls?.[0]?.scoreValue === 10 || (isNoTap && frame?.rolls?.[0]?.scoreValue === 9));
                        return isStrike ? (
                          <RollBox frame={frame} rollIndex={0} isNoTap={isNoTap} />
                        ) : (
                          <>
                            <RollBox frame={frame} rollIndex={0} isNoTap={isNoTap} />
                            <RollBox frame={frame} rollIndex={1} isNoTap={isNoTap} />
                          </>
                        );
                      })()}
                    </div>
                  </td>
                );
              })}
            </tr>
            <tr className="score-row">
              {Array.from({ length: 10 }, (_, i) => (
                <td key={i} className={frameClass(i)}>
                  {runningScores[i] !== null ? runningScores[i] : ''}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RollBox({ frame, rollIndex, isNoTap }) {
  if (!frame || !frame.rolls || rollIndex >= frame.rolls.length) {
    return <span className="roll-box"></span>;
  }

  const text = rollText(frame, rollIndex, isNoTap);
  const isStrike = text === 'X';
  const isSpare = text === '/';

  // Split detection: first roll of a frame, not a strike, check pin pattern
  const isSplitRoll = rollIndex === 0 && !isStrike && frame.rolls[0]?.pinsDown
    && isSplit(frame.rolls[0].pinsDown);

  return (
    <span className={`roll-box ${isStrike ? 'roll-strike' : ''} ${isSpare ? 'roll-spare' : ''} ${isSplitRoll ? 'roll-split' : ''}`}>
      {text}
    </span>
  );
}

/**
 * Compute the standing and missed pins to display for a single roll's pin deck cell.
 * Mirrors the per-rack semantics of BowlingDisplayRules.pinDecksWithMissed10th in iOS:
 *   standing  = first-ball leave of the rack this roll belongs to
 *   missedPins = pins still standing after all balls on that rack (unconverted leave)
 * @param {Object} frame - { number, rolls: [{ pinsDown, scoreValue }] }
 * @param {number} rollIndex - 0-based index within frame.rolls
 * @returns {{ standing: number[], missedPins: number[] }}
 */
export function computePinDeckState(frame, rollIndex, isNoTap = false) {
  if (!frame || !frame.rolls || rollIndex >= frame.rolls.length) {
    return { standing: [], missedPins: [], notapPins: [] };
  }

  const roll = frame.rolls[rollIndex];
  const pinsDown = roll.pinsDown || [];

  // A roll clears the whole rack on a real strike (10) or a no-tap strike
  // (9 pins on a fresh rack). Mirrors iOS BowlingDisplayRules.isNoTapStrike:
  // frames 1-9 only count a no-tap strike on the first ball; the 10th frame
  // defers to isFreshRack10th so each rack's first ball is treated correctly.
  const isNoTapStrike = (idx) => {
    const r = frame.rolls[idx];
    if (!r || !isNoTap || r.scoreValue !== 9) return false;
    return frame.number === 10
      ? isFreshRack10th(frame, idx, isNoTap)
      : idx === 0;
  };
  const clearsRack = (idx) => {
    const r = frame.rolls[idx];
    if (!r) return false;
    if (r.scoreValue === 10) return true;
    return isNoTapStrike(idx);
  };
  // The pin(s) left standing on a no-tap strike, surfaced in the no-tap color
  // (mirrors iOS BowlingDisplayRules.noTapLeftPins). Empty for real strikes.
  const noTapLeave = (idx) => {
    if (!isNoTapStrike(idx)) return [];
    const down = frame.rolls[idx].pinsDown || [];
    const left = [];
    for (let p = 1; p <= 10; p++) {
      if (!down.includes(p)) left.push(p);
    }
    return left;
  };

  // For 10th frame rolls after the first, figure out which pins were reset
  if (rollIndex > 0 && frame.number === 10) {
    const isFresh = isFreshRack10th(frame, rollIndex, isNoTap);

    if (isFresh) {
      // Fresh rack — a strike (real or no-tap) clears it entirely. A no-tap
      // strike surfaces its leftover pin(s) in the no-tap color.
      if (clearsRack(rollIndex)) return { standing: [], missedPins: [], notapPins: noTapLeave(rollIndex) };
      // Otherwise show first-ball leave and outcome for this sub-frame
      const standing = [];
      for (let p = 1; p <= 10; p++) {
        if (!pinsDown.includes(p)) standing.push(p);
      }
      const nextRoll = frame.rolls[rollIndex + 1];
      const missedPins = computeMissedPins(standing, nextRoll, roll.scoreValue);
      return { standing, missedPins, notapPins: [] };
    } else {
      // This roll is not starting a fresh rack — it's the second ball of a rack.
      // Find the rack's first ball and show its leave, colored by what this ball did.
      //   rollIndex=1 not-fresh → rack started at ball 1 (index 0): e.g. 7-/
      //   rollIndex=2 not-fresh → rack started at ball 2 (index 1): e.g. X-6-/
      const rackStartIndex = rollIndex === 1 ? 0 : 1;
      const rackStartPinsDown = frame.rolls[rackStartIndex]?.pinsDown || [];
      const standing = [];
      for (let p = 1; p <= 10; p++) {
        if (!rackStartPinsDown.includes(p)) standing.push(p);
      }
      const missedPins = standing.filter(p => !pinsDown.includes(p));
      return { standing, missedPins, notapPins: [] };
    }
  }

  // rollIndex 0 (any frame) and frames 1-9 second balls.
  // A strike (real or no-tap) clears the rack → empty deck. A no-tap strike
  // surfaces its leftover pin(s) in the no-tap color.
  if (clearsRack(rollIndex)) {
    return { standing: [], missedPins: [], notapPins: noTapLeave(rollIndex) };
  }

  // Standing pins = all 10 minus those knocked down on this roll
  const standing = [];
  for (let p = 1; p <= 10; p++) {
    if (!pinsDown.includes(p)) standing.push(p);
  }

  // Frames 1-9 and the 10th-frame first roll: determine whether the leave was
  // converted (spare) or missed (open) by looking at the second ball.
  const nextRoll = frame.rolls[1];
  const missedPins = computeMissedPins(standing, nextRoll, roll.scoreValue);
  return { standing, missedPins, notapPins: [] };
}

function FramePinDeck({ frame, rollIndex, isNoTap = false, isCurrent = false }) {
  if (!frame || !frame.rolls || rollIndex >= frame.rolls.length) {
    // Nothing rolled here yet. The current frame shows the waiting fresh rack
    // in the accent color (mirrors the iOS current-frame treatment).
    const standing = isCurrent ? [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] : [];
    return <div className="pindeck-mini"><MiniPinDeckSVG standingPins={standing} missedPins={[]} notapPins={[]} isCurrent={isCurrent} /></div>;
  }
  const { standing, missedPins, notapPins } = computePinDeckState(frame, rollIndex, isNoTap);
  return <div className="pindeck-mini"><MiniPinDeckSVG standingPins={standing} missedPins={missedPins} notapPins={notapPins} isCurrent={isCurrent} /></div>;
}

// Determine which standing pins were missed on the follow-up shot
// Returns: [] if spare/strike (all converted), or the pins still standing after second shot
export function computeMissedPins(standingAfterFirst, nextRoll, firstScoreValue) {
  // Strike — no leave to show
  if (firstScoreValue === 10) return [];
  // No second roll yet — no outcome, show neutral
  if (!nextRoll) return [];
  // Spare — all pins converted
  if (firstScoreValue + nextRoll.scoreValue === 10) return [];
  // Open frame — pins still standing after second shot
  const secondPinsDown = nextRoll.pinsDown || [];
  return standingAfterFirst.filter(p => !secondPinsDown.includes(p));
}

// Pin positions in the standard 10-pin triangle layout
// Rendered from bowler's perspective (pin 1 at bottom/front)
const PIN_POSITIONS = {
  7:  { cx: 3, cy: 3 },
  8:  { cx: 9, cy: 3 },
  9:  { cx: 15, cy: 3 },
  10: { cx: 21, cy: 3 },
  4:  { cx: 6, cy: 9 },
  5:  { cx: 12, cy: 9 },
  6:  { cx: 18, cy: 9 },
  2:  { cx: 9, cy: 15 },
  3:  { cx: 15, cy: 15 },
  1:  { cx: 12, cy: 21 },
};

function MiniPinDeckSVG({ standingPins, missedPins = [], notapPins = [], isCurrent = false }) {
  return (
    <svg className="pindeck-svg" viewBox="0 0 24 24" width="38" height="38">
      {Object.entries(PIN_POSITIONS).map(([pin, { cx, cy }]) => {
        const pinNum = parseInt(pin);
        const isNotap = notapPins.includes(pinNum);
        const isStanding = standingPins.includes(pinNum);
        const isMissed = missedPins.includes(pinNum);
        // 5 states: pin-notap (no-tap strike leftover), pin-down (knocked),
        // pin-missed (left and missed), pin-current (standing in the frame
        // being bowled — accent, like iOS), pin-standing (left but converted).
        const className = isNotap
          ? 'pin-notap'
          : (!isStanding ? 'pin-down' : (isMissed ? 'pin-missed' : (isCurrent ? 'pin-current' : 'pin-standing')));
        return (
          <circle
            key={pin}
            cx={cx}
            cy={cy}
            r="2.5"
            className={className}
          />
        );
      })}
    </svg>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="live-stat-card">
      <span className="live-stat-value">{value}</span>
      <span className="live-stat-label">{label}</span>
    </div>
  );
}
